import React, { PropsWithoutRef } from 'react';
import { devWarning } from '@tanium/coreui-utils';
import {
  ButtonSize,
  ButtonVariant,
  createBaseButtonCss,
  disabledVariant,
  sizeVariant,
  VARIANT_PRIMARY,
  VARIANT_SECONDARY,
  variant as variantCss,
} from '@tanium/react-button-base';
import styled from '@tanium/react-emotion';
import { useUsingMouse } from '@tanium/react-mouse-interaction-context';
import { useRouter } from '@tanium/react-router';
import { css } from '@tanium/style-system';
import { ThemeFontWeights } from '@tanium/theme-data';
import {
  LinkHoverText,
  LinkText,
  PrimaryButtonText,
  SecondaryButtonText,
} from './themeDefinitions';

const noOutlineStyle = { ':focus': { outline: 'none' } };

type Anchor = JSX.IntrinsicElements['a'];
type ButtonType = typeof VARIANT_PRIMARY | typeof VARIANT_SECONDARY;

export interface Props extends PropsWithoutRef<Anchor> {
  component?: React.ElementType;
  buttonVariant?: ButtonVariant;
  buttonSize?: ButtonSize;
  disabled?: boolean;
}

const InnerComponent = React.forwardRef(({ component: C = 'a', ...props }: Props, ref) => (
  <C ref={ref} {...props} />
));

/**
 * A styled link component.
 */
const Link = styled(InnerComponent, {
  shouldForwardProp: (p) => p !== 'usingMouse',
})<Props & { usingMouse: boolean }>(
  // TODO: share or consolidate with same styles in coreui-mixins
  css({
    '&&': {
      color: LinkText,
    },
    textDecoration: 'none',
    cursor: 'pointer',
    ':hover': {
      color: LinkHoverText,
      textDecoration: 'underline',
    },
  }),
  ({ usingMouse }) => usingMouse && noOutlineStyle,
);

const ButtonStyledLink = styled(InnerComponent, {
  shouldForwardProp: (p) =>
    p !== 'usingMouse' && p !== 'buttonVariant' && p !== 'buttonSize' && p !== 'disabled',
})<
  Props & {
    usingMouse: boolean;
    buttonVariant: ButtonType;
  }
>(
  createBaseButtonCss,
  css({
    fontWeight: ThemeFontWeights.Bold,
    '&&': {
      textDecoration: 'none',
    },
  }),
  ({ buttonVariant, disabled }) =>
    disabled
      ? css({
          ['&&, &&:hover, &&:active' as string]: disabledVariant(buttonVariant),
        })
      : css({
          '&&': {
            color: buttonVariant === 'primary' ? PrimaryButtonText : SecondaryButtonText,
          },
        }),
  ({ usingMouse }) => usingMouse && noOutlineStyle,
  ({ buttonVariant }) => variantCss(buttonVariant),
  ({ buttonSize }) => sizeVariant(buttonSize),
);

export const LinkBase = React.forwardRef<HTMLAnchorElement, Props>(
  ({ buttonVariant, buttonSize, component = 'a', disabled = false, ...props }, ref) => {
    const { isInternalRoute } = useRouter();
    const usingMouse = useUsingMouse();

    const isLinkVariant = !buttonVariant || buttonVariant === 'link';
    const href = disabled && !isLinkVariant ? undefined : props.href;

    let { tabIndex } = props;
    if (typeof props.tabIndex !== 'number') {
      // If the consumer isn't overriding the tabIndex via Link prop, and
      // if this link lacks an href and is actually being rendered with an anchor tag, set tabIndex
      // explicitly to preserve the tabbable behavior of links with hrefs
      tabIndex = component !== 'a' || href ? undefined : 0;
    }
    if (disabled) {
      tabIndex = undefined;
    }

    let rel: string | undefined;
    if (props.target === '_blank') {
      const isHrefInternal = typeof href === 'string' && isInternalRoute(href);
      rel = isHrefInternal ? 'opener' : 'noreferrer noopener';
    }

    const linkAttributes = {
      href,
      tabIndex,
      rel,
    };

    if (isLinkVariant) {
      devWarning(!disabled, 'The disabled prop is not supported on "link" button variants.');

      return (
        <Link
          component={component}
          ref={ref}
          usingMouse={usingMouse}
          {...props}
          {...linkAttributes}
        />
      );
    }

    return (
      <ButtonStyledLink
        {...props}
        buttonVariant={buttonVariant}
        buttonSize={buttonSize}
        disabled={disabled}
        usingMouse={usingMouse}
        ref={ref}
        {...linkAttributes}
      />
    );
  },
);
